import { ArrowRightIcon } from '@heroicons/react/20/solid'
import { type Athlete } from '@prisma/client'
import { Button, Card } from '@radix-ui/themes'
import {
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Link, redirect, useLoaderData, useSearchParams } from '@remix-run/react'
import React from 'react'
import { useDebounceFetcher } from 'remix-utils/use-debounce-fetcher'
import FlippableCard from '#app/components/FlippableCard.tsx'
import NavigableTable from '#app/components/NavigableTable'
import RankTable from '#app/components/RankTable'
import RunnerComboBox from '#app/components/RunnerComboBox'
import {
	getHistogramData,
	getPeerWindow,
	getRunner,
	getRunnersByName,
} from '#app/queries/RankingAllData-queries'
import { capitalize } from '#app/utils/stringHelpers.ts'

export const meta: MetaFunction = () => [{ title: 'Search Ranking' }]
export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const name = formData.get('search')?.toString()
	const runners = getRunnersByName(name)
	return runners
}

export async function loader({ request }: LoaderFunctionArgs) {
	const searchParams = new URL(request.url).searchParams
	let name = searchParams.get('name')?.toString() || ''

	if (!name) {
		return redirect('/select/search?name=john+korir')
	}

	const runner = await getRunner(name)
	if (!runner) return null

	const [peerWindow, histogramData] = await Promise.all([
		getPeerWindow(runner),
		getHistogramData({ ageClass: runner.ageClass, sex: runner.sex }),
	])

	return { peerWindow, runner, histogramData }
}

export default function Search() {
	const fetcher = useDebounceFetcher<Array<Athlete>>()
	const data = useLoaderData<typeof loader>()
	const [searchParams, setSearchParams] = useSearchParams()

	function handleSearch(e: React.ChangeEvent<HTMLInputElement> | string) {
		let search = ''
		if (typeof e === 'string') search = e
		else search = e.target.value
		fetcher.submit({ search }, { method: 'post', debounceTimeout: 200 })
	}

	function selectName(value: Athlete) {
		if (!value) {
			handleSearch('')
		} else {
			setSearchParams(params => {
				params.set('name', value.firstName + ' ' + value.lastName || '')
				return params
			})
		}
	}

	function isHighlighted(entry: any) {
		return entry?.['fullName'] === searchParams.get('name')
	}

	const mapping = {
		Name: 'fullName',
		Age: 'ageClass',
		Time: 'formattedTime',
	}

	return (
		<>
			<div className="flex justify-center">
				<div className="flex max-w-[700px] flex-1 flex-col items-center gap-y-4">
					<Card>
						<div className="flex flex-1 flex-col items-center gap-y-4">
							<div className="text-center text-h2">Runner Search</div>
							<div>
								Search for an runner's name. If you don't see a result, make sure all the{' '}
								<Link className="text-pink-400 underline" to="/about#qualifications">
									qualifications
								</Link>{' '}
								have been met.
							</div>
							<fetcher.Form className="w-full sm:w-[400px]">
								<RunnerComboBox
									options={fetcher?.data || []}
									renderDisplayValue={athlete => {
										if (!athlete) return ''
										return athlete?.firstName + ' ' + athlete?.lastName
									}}
									onSelection={selectName}
									value={'hello'}
									onTextChange={handleSearch}
								/>
							</fetcher.Form>
							{data?.runner && (
								<Link to={`/athlete/${data?.runner.athlete}`} className="flex">
									<Button variant="outline" color="pink" size="3">
										Go to {capitalize(data?.runner.fullName)}'s athlete page
										<ArrowRightIcon height={15} />
									</Button>
								</Link>
							)}
						</div>
					</Card>
					<FlippableCard
						FrontComponent={<RankTable data={data?.runner} />}
						BackComponent={
							<div className="pb-2">
								<ul className="flex flex-col gap-y-2">
									<li>
										<span className="font-bold underline">Division:</span> Ranking for an individual
										within their category of sex and age.
									</li>
									<li>
										<span className="font-bold underline">Overall:</span> Ranking for an individual
										only with recard to their fastest race time.
									</li>
								</ul>
							</div>
						}
					/>
					<NavigableTable
						columnToHighlight="fullName"
						mapping={mapping}
						isHighlighted={isHighlighted}
						data={data?.peerWindow}
					/>
				</div>
			</div>
		</>
	)
}
